// src/pages/ELearning/ELearningAutoRulesPage.tsx — ФИЧА Y
import React, { useEffect, useState } from 'react';
import { Loader2, Zap, Trash2, Plus, CheckCircle, XCircle, Beaker } from 'lucide-react';
import api, {
  getElearningAutoRules,
  createElearningAutoRule,
  updateElearningAutoRule,
  deleteElearningAutoRule,
} from '../../api/client';
import AutoRuleEditor from '../../components/HRDocuments/AutoRuleEditor';

const EVENT_LABELS: Record<string, string> = {
  'elearning.test_failed': '❌ Тест не пройден',
  'elearning.test_passed': '✅ Тест пройден',
  'elearning.assignment_overdue': '⏰ Назначение просрочено',
  'elearning.path_completed': '🎓 Программа завершена',
};

const ACTION_LABELS: Record<string, string> = {
  'assign_test': '📝 Назначить тест',
  'reassign_test': '🔄 Переназначить тест',
  'assign_path': '🚦 Назначить программу',
  'send_notification': '🔔 Уведомление',
  'send_email': '📧 Email',
  'create_reminder': '⏰ Напоминание',
};

const ELearningAutoRulesPage: React.FC = () => {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getElearningAutoRules();
      setRules(data.items || []);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Удалить правило?')) return;
    try {
      await deleteElearningAutoRule(id);
      setRules(prev => prev.filter(r => r._id !== id));
    } catch (e) { alert('Ошибка удаления'); }
  };

  const handleToggle = async (rule: any) => {
    try {
      await updateElearningAutoRule(rule._id, { active: !rule.active });
      setRules(prev => prev.map(r => r._id === rule._id ? { ...r, active: !r.active } : r));
    } catch (e) { alert('Ошибка'); }
  };

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .ar-hero { animation: fadeUp 0.5s ease-out; }
        .ar-card { animation: fadeUp 0.5s ease-out backwards; transition: all 0.3s ease; }
        .ar-card:hover { transform: translateY(-4px); box-shadow: 0 20px 50px rgba(31, 38, 135, 0.15) !important; }
      `}</style>

      <div className="ar-hero" style={styles.hero}>
        <div style={styles.heroIcon}>
          <Zap size={28} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={styles.title}>Автоправила обучения</h1>
          <p style={styles.subtitle}>Автоматизация: «провалил → назначить повторно» и др.</p>
        </div>
        <button
          onClick={() => { setEditingRule(null); setShowEditor(true); }}
          style={styles.createBtn}
        >
          <Plus size={18} /> Создать правило
        </button>
      </div>

      <div style={styles.infoCard}>
        <strong>💡 Как это работает:</strong> Правило срабатывает при событии в модуле «Обучение».
        Например: при провале теста — автоматически назначается повторное прохождение.
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} className="spin" color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : error ? (
        <div style={styles.errorCard}>{error}</div>
      ) : rules.length === 0 ? (
        <div style={styles.empty}>
          <Zap size={56} color="#667eea" style={{ opacity: 0.4 }} />
          <h3 style={{ color: '#5a5f7a', marginTop: 16, fontWeight: 700 }}>
            Пока нет правил для обучения
          </h3>
          <p style={{ color: '#8b8fa8', fontSize: 14, marginBottom: 24 }}>
            Создайте первое правило автоматизации
          </p>
          <button
            onClick={() => { setEditingRule(null); setShowEditor(true); }}
            style={styles.createBtn}
          >
            <Plus size={16} /> Создать правило
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rules.map((rule, i) => {
            const eventLabel = EVENT_LABELS[rule.trigger?.event] || rule.trigger?.event || '—';
            const actions = rule.actions || [];

            return (
              <div
                key={rule._id}
                className="ar-card"
                style={{ ...styles.card, animationDelay: `${i * 0.05}s` }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div style={styles.ruleName}>{rule.name || 'Без имени'}</div>
                    {rule.description && (
                      <div style={styles.ruleDesc}>{rule.description}</div>
                    )}

                    <div style={styles.chips}>
                      <span style={styles.chipEvent}>
                        <Beaker size={13} /> {eventLabel}
                      </span>
                      {actions.map((a: any, j: number) => (
                        <span key={j} style={styles.chipAction}>
                          {ACTION_LABELS[a.type] || a.type}
                        </span>
                      ))}
                    </div>

                    <div style={styles.metrics}>
                      <span>🔄 Запусков: {rule.runCount || 0}</span>
                      <span>✅ Успешно: {rule.successCount || 0}</span>
                      {rule.failCount > 0 && <span style={{ color: '#ef4444' }}>❌ Ошибок: {rule.failCount}</span>}
                    </div>
                  </div>

                  <div style={styles.actionsCol}>
                    <button
                      onClick={() => handleToggle(rule)}
                      style={{
                        ...styles.statusBtn,
                        background: rule.active
                          ? 'rgba(16,185,129,0.15)'
                          : 'rgba(139,143,168,0.15)',
                        color: rule.active ? '#10b981' : '#8b8fa8',
                      }}
                    >
                      {rule.active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {rule.active ? 'Включено' : 'Выключено'}
                    </button>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => { setEditingRule(rule); setShowEditor(true); }}
                        style={styles.editBtn}
                        title="Редактировать"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(rule._id)}
                        style={styles.deleteBtn}
                        title="Удалить"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* ═══ Модал конструктора ═══ */}
      {showEditor && (
        <AutoRuleEditor
          initialRule={editingRule}
          onSave={async (rule: any) => {
            if (editingRule) {
              await updateElearningAutoRule(editingRule._id, rule);
            } else {
              await createElearningAutoRule(rule);
            }
            setShowEditor(false);
            setEditingRule(null);
            load();
          }}
          onClose={() => { setShowEditor(false); setEditingRule(null); }}
          apiPost={(url: string, data: any) => {
            // Перехватываем URL — заменяем /hr/documents/auto-rules на /elearning/auto-rules
            const rewritten = url.replace('/hr/documents/auto-rules', '/elearning/auto-rules');
            return api.post(rewritten, data);
          }}
        />
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  hero: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 },
  heroIcon: { width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(245,158,11,0.4)', flexShrink: 0 },
  title: { fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #f59e0b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  subtitle: { color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 },

  infoCard: { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', padding: 16, borderRadius: 16, marginBottom: 24, fontSize: 13, color: '#5a5f7a', lineHeight: 1.6 },

  errorCard: { padding: 20, background: 'rgba(239,68,68,0.1)', borderRadius: 12, color: '#ef4444', fontWeight: 600, fontSize: 14 },
  empty: { textAlign: 'center', padding: 60, background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(20px)', borderRadius: 24, border: '1px dashed rgba(245,158,11,0.3)' },
  createBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px', background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)', color: '#fff', borderRadius: 14, textDecoration: 'none', fontWeight: 700, fontSize: 14, boxShadow: '0 8px 24px rgba(245,158,11,0.35)' },

  card: { background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.7)', borderRadius: 20, padding: 20, boxShadow: '0 8px 32px rgba(31,38,135,0.08)' },
  ruleName: { fontSize: 16, fontWeight: 800, color: '#1a1a2e', marginBottom: 6 },
  ruleDesc: { fontSize: 12, color: '#5a5f7a', marginBottom: 10, lineHeight: 1.5 },
  chips: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 },
  chipEvent: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: 'rgba(102,126,234,0.12)', color: '#667eea', borderRadius: 10, fontSize: 11, fontWeight: 700 },
  chipAction: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: 10, fontSize: 11, fontWeight: 700 },
  metrics: { display: 'flex', gap: 14, fontSize: 11, color: '#8b8fa8', fontWeight: 600 },

  actionsCol: { display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' },
  statusBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 12, cursor: 'pointer' },
  deleteBtn: { padding: 8, background: 'rgba(239,68,68,0.08)', border: 'none', color: '#ef4444', borderRadius: 10, cursor: 'pointer' },
  editBtn: { padding: 8, background: 'rgba(102,126,234,0.08)', border: 'none', color: '#667eea', borderRadius: 10, cursor: 'pointer', fontSize: 14 },
};

export default ELearningAutoRulesPage;
